import React, { useState } from 'react';
import { Box, Card, CardContent, Typography, Grid, TextField, Button, CircularProgress, Alert, InputAdornment, Chip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import RefreshIcon from '@mui/icons-material/Refresh';
import GroupIcon from '@mui/icons-material/Group';
import axios from 'axios';
import SingleUser from '../CardComponents/SingleUser';
import { decrypt } from '../../../Security';

const VV = { primary: '#3F2B96', secondary: '#7F5AF0', accent: '#00C896', bg: '#F7F8FC', border: 'rgba(63,43,150,0.10)' };

const SearchField = ({ icon, ...props }) => (
  <TextField variant="outlined" fullWidth size="small"
    InputProps={icon ? { startAdornment: <InputAdornment position="start">{React.cloneElement(icon, { sx: { color: VV.secondary, fontSize: 18 } })}</InputAdornment> } : undefined}
    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#fff', '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: VV.secondary }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: VV.secondary, borderWidth: 2 } } }}
    {...props}
  />
);

const UserSearch = ({ setUserId }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [users, setUsers] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const searchUser = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(process.env.REACT_APP_PUBLIC_SERVER_URI + '/adminWeb/users/', {
        params: { name, email },
        withCredentials: true
      });
      const data = decrypt(response.data);
      setUsers(data.users);
    } catch (error) {
      console.error('Error:', error);
      setError('Failed to search users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetSearch = () => {
    setName('');
    setEmail('');
    setUsers(null);
    setError(null);
  };

  return (
    <Box sx={{ bgcolor: VV.bg, minHeight: '85vh', pb: 4 }}>
      <Box sx={{ background: `linear-gradient(135deg, ${VV.primary} 0%, ${VV.secondary} 100%)`, pt: 4, pb: 8, px: { xs: 2, md: 5 } }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <GroupIcon sx={{ color: VV.accent }} />
            <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.75)', letterSpacing: 2 }}>User Search</Typography>
          </Box>
          <Typography variant="h4" sx={{ color: '#fff', fontWeight: 800 }}>Find Users</Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.65)', mt: 0.5, fontSize: '0.9rem' }}>Search users by name or email address</Typography>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 4 }, mt: -5 }}>
        <Card sx={{ borderRadius: '18px', boxShadow: '0 20px 60px rgba(63,43,150,0.18)', mb: 3 }}>
          <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} md={6}>
                <SearchField icon={<PersonIcon />} label="Name" value={name} onChange={e => setName(e.target.value)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <SearchField icon={<EmailIcon />} label="Email" value={email} onChange={e => setEmail(e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  <Button startIcon={<RefreshIcon />} onClick={resetSearch} variant="outlined"
                    sx={{ borderRadius: '10px', borderColor: VV.border, color: '#666', '&:hover': { borderColor: '#aaa' } }}>Reset</Button>
                  <Button startIcon={<SearchIcon />} onClick={searchUser} variant="contained" disabled={loading}
                    sx={{ borderRadius: '10px', px: 4, background: `linear-gradient(135deg, ${VV.primary}, ${VV.secondary})`, fontWeight: 700, boxShadow: '0 4px 16px rgba(63,43,150,0.35)' }}>
                    {loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Search'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {error && <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>{error}</Alert>}

        {users && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: VV.primary }}>Search Results</Typography>
              <Chip label={`${users.length} found`} sx={{ bgcolor: `${VV.accent}22`, color: VV.accent, fontWeight: 700, borderRadius: '8px' }} />
            </Box>
            <Grid container spacing={3}>
              {users.map((user) => (
                <Grid item xs={12} key={user._id}>
                  <SingleUser user={user} setUserId={setUserId} />
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {users && users.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <GroupIcon sx={{ fontSize: 64, color: '#ccc', mb: 2, display: 'block', mx: 'auto' }} />
            <Typography color="text.secondary">No users found matching your search criteria</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default UserSearch;
